import {
  faArrowsRepeat,
  faChevronDoubleDown,
  faChevronDoubleUp,
  faExclamationTriangle,
  faLocationDotSlash,
  faLockAlt,
  faNoteSticky,
  faRotateExclamation,
  faUserMinus,
  faUserPlus,
  faUserXmark,
  faVideo
} from '@fortawesome/pro-light-svg-icons';
import { Box, SxProps } from '@mui/material';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import { DARK, ORANGE, WHITE } from 'constants/colors';
import { isValidElement } from 'react';
import {
  LocationType,
  SchedulingLabel,
  TimeWindowResponse
} from 'generated/index';
import NonRecurringIcon from '../NonRecurringIcon/NonRecurringIcon';
import styled from 'styled-components';

type SignalRowProps = {
  schedulingLabel?: SchedulingLabel;
  addressNotFound?: boolean;
  description?: string;
  excludedEmployeeClientCombination?: boolean;
  hasOverlap?: boolean;
  isAbsent?: boolean;
  isInSyncWithEcd?: boolean;
  isRecurring?: boolean;
  locationType?: LocationType;
  overQualified?: boolean;
  selected?: boolean;
  signalThreshold: number;
  startsTooEarly?: boolean;
  startsTooLate?: boolean;
  timeWindow?: TimeWindowResponse;
  underQualified?: boolean;

  maxWidth?: number;
  sx?: SxProps;
};

const IconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
  &:first-of-type {
    margin-left: -3px;
  }
`;

const ActivitySignalRow = ({
  children,
  threshold,
  maxWidth = 0
}: {
  children: unknown[];
  threshold: number;
  maxWidth?: number;
}) => {
  const ICON_WIDTH = 20;
  const hasMaxWidth = maxWidth && maxWidth > 0;

  // Filter out any non-React elements first (like booleans due to conditional rendering)
  const elements: JSX.Element[] = children.filter((_: unknown) =>
    isValidElement(_)
  ) as JSX.Element[];

  const maxWidthThreshold = Math.floor(maxWidth / ICON_WIDTH);
  const thresholdExceeded =
    elements.length > (hasMaxWidth ? maxWidthThreshold : threshold);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="18px"
    >
      {elements.filter((_: JSX.Element, i: number) => {
        if (hasMaxWidth) return i <= maxWidthThreshold - 1;
        return i <= threshold - 1;
      })}
      {thresholdExceeded && (
        <Box
          display="flex"
          alignItems="center"
          sx={{
            height: '15px',
            width: '15px',
            paddingLeft: '2px',
            borderRadius: '50%',
            boxSizing: 'border-box',
            backgroundColor: ORANGE,
            color: WHITE,
            fontSize: '9px',
            cursor: 'pointer'
          }}
        >
          +{elements.length - (hasMaxWidth ? maxWidthThreshold : threshold)}
        </Box>
      )}
    </Box>
  );
};

const SignalRow = ({
  schedulingLabel,
  addressNotFound,
  description,
  excludedEmployeeClientCombination,
  hasOverlap,
  isInSyncWithEcd,
  isRecurring,
  locationType,
  overQualified,
  signalThreshold,
  startsTooEarly,
  startsTooLate,
  timeWindow,
  underQualified,
  maxWidth = 0,
  sx
}: SignalRowProps) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      sx={{
        boxSizing: 'border-box',
        padding: '2px 0 2px 8px',
        width: '80px',
        ...(sx || {})
      }}
    >
      <ActivitySignalRow threshold={signalThreshold} maxWidth={maxWidth}>
        {schedulingLabel === 'VV' && isRecurring && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faArrowsRepeat}
              sx={{ fontSize: '11px', color: DARK }}
            />
          </IconContainer>
        )}
        {schedulingLabel === 'VV' && !isRecurring && <NonRecurringIcon />}
        {startsTooLate && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faChevronDoubleUp}
              sx={{
                fontSize: '11px',
                color: DARK
              }}
            />
          </IconContainer>
        )}
        {startsTooEarly && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faChevronDoubleDown}
              sx={{
                fontSize: '11px',
                color: DARK
              }}
            />
          </IconContainer>
        )}
        {timeWindow?.isUrgent && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faLockAlt}
              color="error"
              sx={{
                fontSize: '12px',
                color: DARK
              }}
            />
          </IconContainer>
        )}
        {locationType === 'TELECARE' && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faVideo}
              color="error"
              sx={{
                fontSize: '12px',
                color: DARK
              }}
            />
          </IconContainer>
        )}
        {description && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faNoteSticky}
              color="error"
              sx={{
                fontSize: '12px',
                color: DARK
              }}
            />
          </IconContainer>
        )}
        {excludedEmployeeClientCombination && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faUserXmark}
              sx={{ fontSize: '14px', color: DARK }}
            />
          </IconContainer>
        )}
        {underQualified && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faUserMinus}
              sx={{ fontSize: '14px', color: DARK }}
            />
          </IconContainer>
        )}
        {overQualified && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faUserPlus}
              sx={{ fontSize: '14px', color: DARK }}
            />
          </IconContainer>
        )}
        {!isInSyncWithEcd && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faRotateExclamation}
              sx={{ fontSize: '14px', color: DARK }}
            />
          </IconContainer>
        )}
        {addressNotFound && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faLocationDotSlash}
              sx={{ fontSize: '14px', color: DARK }}
            />
          </IconContainer>
        )}
        {hasOverlap && (
          <IconContainer>
            <FontAwesomeSvgIcon
              icon={faExclamationTriangle}
              sx={{ fontSize: '14px', color: DARK }}
            />
          </IconContainer>
        )}
      </ActivitySignalRow>
    </Box>
  );
};

export default SignalRow;
